import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { persist } from 'zustand/middleware'
import { createSelectors } from '../utils/createSelector'
import { emailRegex } from '../../constants'
import { ContractType } from '../../types/ContractType'
import { getCurrentDateFormated } from '../../Utils/Date'

type UIState = {
  email: string
  firstName: string
  lastName: string
  cv: File | null
  message: string
  agreed: boolean
  errors: Record<string, string>
  isDirty: boolean
  office: string | null
  jobType: string | null
  contractType: ContractType | null
  startDate: string | null
}

type UIActions = {
  resetState: () => void
  setFieldValue: <K extends keyof UIState>(field: K, value: UIState[K]) => void
  setFormErrors: (errors: Record<string, string>) => void
  validateForm: (isSpontaneous?: boolean) => void
  setDirty: (isDirty: boolean) => void
  setOffice: (office: string) => void
  resetStateButkeepPersonalInfo: () => void
}

type State = UIState & UIActions

const defaultState: UIState = {
  email: '',
  firstName: '',
  lastName: '',
  cv: null,
  message: '',
  agreed: false,
  errors: {},
  isDirty: false,
  office: null,
  jobType: null,
  contractType: 'cdi',
  startDate: getCurrentDateFormated(),
}

export const useApplyFormStore = createSelectors(
  create<State>()(
    persist(
      immer((set) => ({
        ...defaultState,

        validateForm: (isSpontaneous?: boolean) =>
          set((state) => {
            const { firstName, lastName, email, cv } = state

            state.errors = {}
            if (!firstName) {
              state.errors.firstName = 'Veuillez entrer votre prénom.'
            } else if (!/^[a-zA-ZÀ-ÿ0-9\s'-/]*$/u.test(firstName)) {
              state.errors.firstName =
                'Le prénom contient un ou des caractères invalides.'
            }

            if (!lastName) {
              state.errors.lastName = 'Veuillez entrer votre nom.'
            } else if (lastName && !/^[a-zA-ZÀ-ÿ0-9\s'-/]*$/u.test(lastName)) {
              state.errors.lastName =
                'Le nom contient un ou des caractères invalides.'
            }

            if (!email) {
              state.errors.email = 'Veuillez entrer votre adresse e-mail.'
            } else if (!emailRegex.test(email)) {
              state.errors.email = 'Veuillez entrer une adresse e-mail valide.'
            }

            if (!cv) {
              state.errors.cv = 'Veuillez sélectionner votre CV.'
            } else if (cv.size > 3145728) {
              state.errors.cv =
                'Veuillez sélectionner un fichier de moins de 3Mo.'
            } else if (!/\.(pdf|doc|docx|png)$/i.test(cv.name)) {
              state.errors.cv =
                'Veuillez sélectionner un fichier PDF, DOCX, ou PNG.'
            }

            if (isSpontaneous) {
              if (!state.office) {
                state.errors.office = 'Veuillez sélectionner un bureau.'
              }

              if (!state.jobType) {
                state.errors.jobType = 'Veuillez sélectionner un type de poste.'
              }

              if (!state.contractType) {
                state.errors.contractType =
                  'Veuillez sélectionner un type de contrat'
              }

              if (!state.startDate) {
                state.errors.startDate =
                  'Veuillez sélectionner une date de début.'
              }
            }
          }),

        resetState: () => set(defaultState),
        setFieldValue: (field, value) => set({ isDirty: true, [field]: value }),

        setFormErrors: (errors) => set({ errors }),

        setDirty: (isDirty) => set({ isDirty }),

        setOffice: (office) =>
          set({
            office,
          }),

        resetStateButkeepPersonalInfo: () =>
          set({
            message: defaultState.message,
            agreed: defaultState.agreed,
            errors: defaultState.errors,
            isDirty: defaultState.isDirty,
            office: defaultState.office,
            startDate: defaultState.startDate,
            jobType: defaultState.jobType,
            contractType: defaultState.contractType,
            cv: defaultState.cv,
          }),
      })),
      {
        name: 'apply-form-state-storage',
      },
    ),
  ),
)
